/**
 * Parent/Child tab — the whole family this conversation belongs to, drawn as a
 * tree: the parent on top and every child hanging off it, with the
 * conversation being viewed highlighted. A child therefore sees its parent and
 * all of its siblings, not just the one link it holds.
 *
 * Parent and self come from GET /conversations/:id; the children from the list
 * endpoint filtered by ?parentId= (archived ones included, like the inbox).
 * Opening a node opens that conversation in its own agent tab.
 */
import { useEffect, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Network, GitBranch, User, Calendar, ExternalLink, MapPin, Loader2 } from 'lucide-react'
import { api } from '../../../api/client.js'
import { R } from './primitives.js'
import { formatInTimezone } from '../../../utils/timezone.js'
import { formatCustomerName } from '../../../utils/formatCustomerName.js'
import { useConversationStatuses } from '../../../hooks/useConversationStatuses.js'
import { presentConversationStatus, statusBucket, statusStyles } from '../../../modules/conversations/statusCatalog.js'

interface ParentChildTabProps {
  conversationId: string
  parentId: string | null
  isParent: boolean
  /** Kept for callers; nodes now open in their own tab instead. */
  onNavigateToConversation?: (conversationId: string) => void
  onCreateChild: () => void
  canCreateChild?: boolean | undefined
  createChildDisabledReason?: string | undefined
}

/** Normalized node shape used for every card in the tree. */
interface TreeNode {
  id: string
  referenceId: string
  subject: string | null
  status: string
  conversationStatusId: string | null
  statusLabel: string | null
  statusColor: string | null
  client: string | null
  agent: string | null
  priority: string | null
  channel: string | null
  openedAt: string | null
}

interface DetailRow {
  id: string; referenceId: string; subject: string | null; status: string; openedAt: string | null
  displayName: string | null; primaryHandle: string | null; assignedAgentName: string | null
  channelOrigin: string | null; conversationStatusId?: string | null
}

interface ListRow {
  id: string; referenceId: string; subject: string | null; status: string; openedAt: string | null
  displayName: string | null; primaryHandle: string | null; priorityLabel: string | null
  assignedAgentLoginId: string | null; channelOrigin: string | null
  conversationStatusId?: string | null; statusLabel?: string | null; statusColor?: string | null
}

function fromDetail(p: DetailRow): TreeNode {
  return {
    id: p.id, referenceId: p.referenceId, subject: p.subject, status: p.status,
    conversationStatusId: p.conversationStatusId ?? null, statusLabel: null, statusColor: null,
    client: formatCustomerName(p.displayName, p.primaryHandle, p.channelOrigin),
    agent: p.assignedAgentName, priority: null, channel: p.channelOrigin, openedAt: p.openedAt,
  }
}

function fromList(c: ListRow): TreeNode {
  return {
    id: c.id, referenceId: c.referenceId, subject: c.subject, status: c.status,
    conversationStatusId: c.conversationStatusId ?? null,
    statusLabel: c.statusLabel ?? null, statusColor: c.statusColor ?? null,
    client: formatCustomerName(c.displayName, c.primaryHandle, c.channelOrigin),
    agent: c.assignedAgentLoginId, priority: c.priorityLabel, channel: c.channelOrigin, openedAt: c.openedAt,
  }
}

async function loadChildren(parentId: string): Promise<TreeNode[]> {
  const byId = new Map<string, TreeNode>()
  for (const filter of ['all', 'archived']) {
    let offset = 0
    do {
      const params = new URLSearchParams({ parentId, filter, limit: '200', offset: String(offset) })
      const res = await api.get<{ items: ListRow[]; nextOffset: number | null }>(`/conversations?${params.toString()}`)
      for (const row of res.items ?? []) byId.set(row.id, fromList(row))
      offset = res.nextOffset ?? -1
    } while (offset >= 0)
  }
  return [...byId.values()]
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return formatInTimezone(d, { day: '2-digit', month: 'short', year: 'numeric' })
}

function StatusPill({ node }: { node: TreeNode }): JSX.Element {
  const { statuses } = useConversationStatuses()
  const present = presentConversationStatus(statuses, {
    status: node.status,
    conversationStatusId: node.conversationStatusId,
    statusName: node.statusLabel,
    statusColor: node.statusColor,
  })
  const styles = statusStyles(present.color)
  return (
    <span className={`inline-flex items-center gap-1.5 ${R} bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700`} style={styles.pill}>
      <span className={`h-1.5 w-1.5 rounded-full ${present.color ? '' : 'bg-slate-400'}`} style={styles.dot} />
      {present.label}
    </span>
  )
}

function NodeCard({ node, kind, current, onOpen }: {
  node: TreeNode
  kind: 'parent' | 'child'
  current: boolean
  onOpen: (node: TreeNode) => void
}): JSX.Element {
  const Icon = kind === 'parent' ? Network : GitBranch
  return (
    <button
      type="button"
      onClick={() => { if (!current) onOpen(node) }}
      aria-current={current ? 'true' : undefined}
      title={current ? 'You are viewing this conversation' : `Open #${node.referenceId} in a new tab`}
      className={`group relative flex w-full items-start gap-3 ${R} border px-4 py-3 text-left transition-all ${
        current
          ? 'cursor-default border-[#3366FF] bg-[#F5F8FF] shadow-[0_0_0_3px_rgba(51,102,255,0.12)]'
          : 'border-slate-200 bg-white hover:border-[#3366FF]/60 hover:shadow-sm'
      }`}
    >
      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center ${R} ${
        kind === 'parent' ? 'bg-indigo-50 text-indigo-600' : 'bg-violet-50 text-violet-600'
      }`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`${R} px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
            kind === 'parent' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'
          }`}>
            {kind === 'parent' ? 'Parent' : 'Child'}
          </span>
          <span className="font-mono text-sm font-semibold text-slate-800">#{node.referenceId}</span>
          <StatusPill node={node} />
          {node.priority && (
            <span className={`${R} bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-[#3366FF]`}>{node.priority}</span>
          )}
          {node.channel && (
            <span className={`${R} bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500`}>{node.channel}</span>
          )}
          {current && (
            <span className={`inline-flex items-center gap-1 ${R} bg-[#3366FF] px-2 py-0.5 text-[10px] font-bold text-white`}>
              <MapPin className="h-3 w-3" />You are here
            </span>
          )}
        </div>
        <div className="mt-1 truncate text-sm font-medium text-slate-800">{node.subject || '(No subject)'}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400">
          <span className="truncate text-slate-500">{node.client || 'Unknown'}</span>
          {node.agent && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{node.agent}</span>}
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(node.openedAt)}</span>
        </div>
      </div>
      {!current && (
        <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-[#3366FF]" />
      )}
    </button>
  )
}

export default function ParentChildTab({
  conversationId,
  parentId,
  isParent,
  onCreateChild,
  canCreateChild = true,
  createChildDisabledReason,
}: ParentChildTabProps): JSX.Element {
  const navigate = useNavigate()
  // The tree's root: the parent when viewing a child, otherwise this conversation.
  const rootId = parentId ?? (isParent ? conversationId : null)
  const [root, setRoot] = useState<TreeNode | null>(null)
  const [children, setChildren] = useState<TreeNode[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!rootId) { setRoot(null); setChildren([]); return }
    let cancelled = false
    setLoading(true)
    setFailed(false)
    Promise.all([
      api.get<{ conversation: DetailRow }>(`/conversations/${rootId}`).then((res) => fromDetail(res.conversation)),
      loadChildren(rootId),
    ])
      .then(([rootNode, kids]) => {
        if (cancelled) return
        setRoot(rootNode)
        setChildren(kids)
      })
      .catch(() => { if (!cancelled) setFailed(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [rootId])

  const openInTab = (node: TreeNode): void => {
    navigate(`/agent/conversation/view/${node.id}`, { state: { tabLabel: `#${node.referenceId}` } })
  }

  const closedCount = children.filter((c) => statusBucket(c.status) === 'closed').length

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-5">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Parent / Child</h3>
          <p className="text-xs text-slate-400">
            {rootId && children.length > 0
              ? `1 parent · ${children.length} child${children.length === 1 ? '' : 'ren'} · ${closedCount} of ${children.length} closed`
              : 'Related conversations nested under or above this one'}
          </p>
        </div>
        <button
          type="button"
          onClick={onCreateChild}
          disabled={!canCreateChild}
          title={createChildDisabledReason}
          className={`inline-flex shrink-0 items-center gap-1.5 ${R} border border-slate-200 bg-white px-3 py-2 text-xs font-semibold transition-colors ${
            canCreateChild
              ? 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              : 'cursor-not-allowed text-slate-300'
          }`}
        >
          <Network className={`h-4 w-4 ${canCreateChild ? 'text-[#3366FF]' : 'text-slate-300'}`} />
          Add child
        </button>
      </div>

      {rootId && loading && !root && (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />Loading relationships…
        </div>
      )}

      {rootId && failed && !loading && (
        <div className={`${R} border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600`}>
          Could not load the related conversations.
        </div>
      )}

      {root && (
        <div>
          <NodeCard node={root} kind="parent" current={root.id === conversationId} onOpen={openInTab} />

          {children.length === 0 ? (
            <div className="ml-[31px] border-l-2 border-dashed border-slate-200 pl-6 pt-3">
              <div className={`flex flex-col items-center justify-center gap-2 ${R} border border-dashed border-slate-200 bg-white py-8 text-center`}>
                <GitBranch className="h-6 w-6 text-slate-300" />
                <p className="text-sm text-slate-400">No child conversations yet.</p>
              </div>
            </div>
          ) : (
            // Tree connectors: a trunk drops from the parent's icon column and
            // an elbow branches into each child; the trunk stops at the last one.
            <ul className="ml-[31px] pt-3">
              {children.map((child, i) => {
                const last = i === children.length - 1
                return (
                  <li key={child.id} className={`relative pl-8 ${last ? '' : 'pb-2.5'}`}>
                    <span aria-hidden className="absolute -top-3 left-0 h-[calc(1.5rem+18px)] w-6 rounded-bl-[6px] border-b-2 border-l-2 border-violet-200" />
                    {!last && <span aria-hidden className="absolute bottom-0 left-0 top-0 border-l-2 border-violet-200" />}
                    <NodeCard node={child} kind="child" current={child.id === conversationId} onOpen={openInTab} />
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}

      {!rootId && (
        <div className={`flex flex-col items-center justify-center gap-2 ${R} border border-dashed border-slate-200 bg-white py-12 text-center`}>
          <Network className="h-7 w-7 text-slate-300" />
          <p className="text-sm text-slate-400">This conversation has no parent or child conversations.</p>
        </div>
      )}
    </div>
  )
}
